import { Modal, Form, validator, field, error, IFormContext } from 'rewire-ui';
import { isNullOrUndefined } from 'rewire-common';
import { freeze } from 'rewire-core';
import { ConfirmUserInput, passwordRegEx, PhoneNumberInput } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import { history } from '../RouterWithHistory';
import Routes from '../Routes';
import { notifications } from '../ui/Notifications';
import { dataUrlToFile } from '../utility/DataConverters';
import { nameSearcher } from '../utility/Searchers';

interface ISetupAccountModelState {
  isComplete: boolean;
  isExistingOrganization: boolean;
  isSingleSignOn: boolean;
  token: string;
}

export class SetupAccountModel extends Modal<ISetupAccountModelState> {
  form: Form;

  constructor() {
    super('');
    const applicationTemplateSearcher = nameSearcher(DataQueries.getApplicationTemplatesSearch);
    this.form = Form.create(
      (form: IFormContext) => ({
        name: form.string().label('Name *').validators('required').placeholder('enter your name').autoFocus(),
        email: form.email().label('Email *').validators('required').placeholder('enter your email'),
        password: form
          .password()
          .label('Password *')
          .validators('required', passwordRegEx, validator('==', field('confirmPassword'), error('passwords are not the same')))
          .placeholder('enter your password'),
        confirmPassword: form.password().label('Confirm Password *').validators('required').placeholder('confirm your password'),
        organization: form.string().label('Organization *').placeholder('enter your organization').validators('required'),
        location: form.string().label('Location *').placeholder('enter your location').validators('required'),
        phone: form.phone().label('Phone Number (Optional)'),
        applicationTemplate: form
          .select(applicationTemplateSearcher)
          .label('Starter Template (Optional)')
          .placeholder('enter a starter template'),
        avatar: form.avatar({ width: 1024, height: 768, avatarDiameter: 200, cropRadius: 100 }).label('Add Photo'),
        avatarMobile: form.avatar({ width: 230, height: 230, avatarDiameter: 175, cropRadius: 75 }).label('Add Photo'),
      }),
      {},
      { validateOnUpdate: false },
    );
    this.action('continue', this.submit, { type: 'submit' });
  }

  get isComplete(): boolean {
    return this.state.isComplete;
  }

  get isExistingOrganization(): boolean {
    return this.state.isExistingOrganization;
  }

  get isSingleSignOn(): boolean {
    return this.state.isSingleSignOn;
  }

  get token(): string {
    return this.state.token;
  }

  reset() {
    this.form.reset();
    freeze(() => {
      this.state.isComplete = false;
      this.state.isExistingOrganization = false;
      this.state.isSingleSignOn = false;
      this.state.token = '';
    });
  }

  load = async (token: string) => {
    try {
      const result = await DataQueries.getConfirmationStatus(token);
      if (!result) {
        notifications.error('Invalid Credentials, Redirecting to login');
        await this.redirectToLogin();
        return;
      }

      this.state.isExistingOrganization = !!result.organization;
      this.state.token = token;
      this.form.value = { email: result.email, name: result.name, phone: result.phone };
      this.form.field.email.disabled = () => !isNullOrUndefined(result!.email);
      this.form.field.password.disableErrors = this.isSingleSignOn;
      this.form.field.confirmPassword.disableErrors = this.isSingleSignOn;
      this.form.field.applicationTemplate.disableErrors = this.isExistingOrganization;
      this.form.field.organization.disableErrors = this.isExistingOrganization;
      this.form.field.location.disableErrors = this.isExistingOrganization;
      this.open();
    } catch {
      await this.redirectToLogin();
      return;
    }
  };

  async redirectToLogin() {
    this.reset();
    history.push(Routes.Login);
  }

  private setupAccount = async (): Promise<boolean> => {
    const avatar = this.form.field.avatar.value || this.form.field.avatarMobile.value;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userProfile: ConfirmUserInput = {
      name: this.form.field.name.value,
      avatar: avatar && (!avatar.match(/^data:image/) ? avatar : dataUrlToFile(this.form.field.avatar.value, 'avatar.png')),
      phone: { home: this.form.field.phone.value && String(this.form.field.phone.value) } as PhoneNumberInput,
      organization: this.form.field.organization.value,
      location: this.form.field.location.value,
      applicationTemplate: this.state.isExistingOrganization ? undefined : this.form.field.applicationTemplate.value?.id,
      timezone,
    };

    const result = this.isSingleSignOn
      ? await DataMutations.confirmSingleSignOnUser(this.token, this.form.field.email.value, userProfile)
      : await DataMutations.confirmUser(this.token, this.form.field.email.value, this.form.field.password.value, userProfile);

    if (result) {
      // we have sessionId returned here, so we can automatically login instead of redirecting to the login screen if we want to.
      notifications.success('Account Creation Successful');
      this.state.isComplete = true;
      return true;
    }

    notifications.error('Account Creation Failed');
    return false;
  };

  submit = async () => {
    return await this.form.submitAsync(this.setupAccount);
  };
}

const setupAccountModel = new SetupAccountModel();
export default setupAccountModel;
